import { useEffect, useMemo, useRef } from 'react';
import type { Mention } from '@fluenceur/types';
import { useMentionNavigatorStore } from '@/stores/mention-navigator';

interface UseMentionNavigatorSyncOptions {
  contextKey: string;
  mentions: Mention[];
  total: number;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
}

export const useMentionNavigatorSync = ({
  contextKey,
  mentions,
  total,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
}: UseMentionNavigatorSyncOptions) => {
  const mentionIds = useMemo(() => mentions.map((m) => m.id), [mentions]);
  const idsKey = mentionIds.join(',');

  const fetchNextPageRef = useRef(fetchNextPage);
  useEffect(() => {
    fetchNextPageRef.current = fetchNextPage;
  }, [fetchNextPage]);

  useEffect(() => {
    useMentionNavigatorStore.getState().setContext({
      contextKey,
      mentionIds,
      total,
      hasNextPage,
      isFetchingNextPage,
      fetchNextPage: () => fetchNextPageRef.current(),
    });
    // `mentionIds` omitted because `idsKey` already captures identity — including
    // the array would retrigger on every render since `useMemo` returns a new ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contextKey, idsKey, total, hasNextPage, isFetchingNextPage]);

  useEffect(() => {
    return () => {
      useMentionNavigatorStore.getState().clear(contextKey);
    };
  }, [contextKey]);
};
